import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Button,
  Box,
  Typography,
  Avatar,
  Stack,
  Autocomplete,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { s3baseUrl } from "../../config/config";
import { TimeZones } from "../../bi_lib/timeZones";

const statusOptions = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];
const twoFaOptions = [
  { value: true, label: "Enabled" },
  { value: false, label: "Disabled" },
];

const passwordRequirements = [
  {
    id: "minLength",
    label: "At least 8 characters",
    test: (p) => p.length >= 8,
  },
  // {
  //   id: "hasUpper",
  //   label: "One uppercase letter",
  //   test: (p) => /[A-Z]/.test(p),
  // },
  // {
  //   id: "hasLower",
  //   label: "One lowercase letter",
  //   test: (p) => /[a-z]/.test(p),
  // },
  // { id: "hasNumber", label: "One number", test: (p) => /\d/.test(p) },
  // {
  //   id: "hasSymbol",
  //   label: "One special character",
  //   test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  // },
];
const validatePassword = (password) => {
  const validations = {};
  passwordRequirements.forEach((req) => {
    validations[req.id] = req.test(password);
  });

  return {
    isValid: Object.values(validations).every(Boolean),
    validations,
  };
};

const formatFileName = (name) => {
  if (!name || name === "No file chosen" || name === "Current image")
    return name;
  const lastDotIndex = name.lastIndexOf(".");
  if (lastDotIndex === -1) return name;

  const extension = name.substring(lastDotIndex);
  const baseName = name.substring(0, lastDotIndex);

  if (baseName.length <= 8) return name;

  const first4 = baseName.substring(0, 4);
  const last4 = baseName.substring(baseName.length - 4);

  return `${first4}...${last4}${extension}`;
};

export default function DelegateForm({ delegate, onChange, isEdit = false }) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    contact_number: "",
    address: "",
    city: "",
    state: "",
    main_heading: "",
    biography: "",
    status: true,
    is_2f_enabled: false,
    image: null,
    time_zone: "Europe/Dublin",
    ...delegate,
  });

  const [localPreview, setLocalPreview] = useState(null);
  const [s3ImageUrl, setS3ImageUrl] = useState(null);
  const [fileName, setFileName] = useState("No file chosen");
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    validations: {},
  });

  useEffect(() => {
    if (delegate?.image?.thumbnail_1) {
      const imageUrl = s3baseUrl + delegate.image.thumbnail_1;
      setS3ImageUrl(imageUrl);

      // Extract filename from path or use name property if available
      const rawName =
        delegate.image.name || delegate.image.thumbnail_1.split("/").pop();
      setFileName(formatFileName(rawName));

      setLocalPreview(null);
    } else {
      setS3ImageUrl(null);
      if (!localPreview) {
        setFileName("No file chosen");
      }
    }
  }, [delegate]);

  useEffect(() => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      contact_number: "",
      address: "",
      city: "",
      state: "",
      main_heading: "",
      biography: "",
      status: true,
      is_2f_enabled: false,
      image: null,
      time_zone: "Europe/Dublin",
      ...delegate,
    });
  }, [delegate]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;

    if (field === "contact_number") {
      // Allow only numbers and symbols: + - ( )
      value = value.replace(/[^0-9+\-()]/g, "");
    }

    const newData = {
      ...formData,
      [field]: value,
    };
    setFormData(newData);
    onChange(newData);

    if (field === "password") {
      setPasswordValidation(validatePassword(value));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Revoke old preview URL if it exists
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
      const previewUrl = URL.createObjectURL(file);
      setLocalPreview(previewUrl);
      setS3ImageUrl(null); // Clear S3 image when new file is selected
      setFileName(formatFileName(file.name));

      const newData = {
        ...formData,
        image: file,
      };
      setFormData(newData);
      onChange(newData);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  return (
    <Grid container spacing={3}>
      {/* Image Upload - First */}
      <Grid item xs={12} mb={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={localPreview || s3ImageUrl || undefined}
            alt={formData.first_name}
            sx={{
              width: 84,
              height: 84,
              bgcolor: "grey.300",
              color: "grey.600",
              fontSize: "0.75rem",
            }}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{
              color: "text.primary",
              borderColor: "grey.300",
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: "grey.100",
              },
            }}
          >
            Choose Image
            <Box component="span" sx={{ ml: 0.5 }}>
              *
            </Box>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!s3ImageUrl && !localPreview}
              style={{
                clip: "rect(0 0 0 0)",
                clipPath: "inset(50%)",
                height: 1,
                overflow: "hidden",
                position: "absolute",
                bottom: 0,
                left: 0,
                whiteSpace: "nowrap",
                width: 1,
              }}
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {fileName}
          </Typography>
        </Box>
      </Grid>

      {/* Status - Second */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label="Status"
          value={formData.status !== undefined ? formData.status : true}
          onChange={handleChange("status")}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label="Two Factor Authentication"
          value={
            formData.is_2f_enabled !== undefined ? formData.is_2f_enabled : true
          }
          onChange={handleChange("is_2f_enabled")}
        >
          {twoFaOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Rest of the fields */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name"
          value={formData.first_name || ""}
          onChange={handleChange("first_name")}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={formData.last_name || ""}
          onChange={handleChange("last_name")}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.email || ""}
          onChange={handleChange("email")}
          disabled={isEdit}
          required
        />
      </Grid>
      {!isEdit && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            value={formData.password || ""}
            onChange={handleChange("password")}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}
      {!isEdit && formData.password && !passwordValidation.isValid && (
        <Grid item xs={12}>
          <Box sx={{ px: 2, py: 1, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
            >
              Password requirements:
            </Typography>
            <Stack spacing={0.5}>
              {passwordRequirements.map((req) => (
                <Stack
                  key={req.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: passwordValidation.validations[req.id]
                        ? "success.main"
                        : "grey.400",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: passwordValidation.validations[req.id]
                        ? "success.main"
                        : "text.secondary",
                    }}
                  >
                    {req.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Contact Number"
          value={formData.contact_number || ""}
          onChange={handleChange("contact_number")}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address"
          value={formData.address || ""}
          onChange={handleChange("address")}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="City"
          value={formData.city || ""}
          onChange={handleChange("city")}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="State"
          value={formData.state || ""}
          onChange={handleChange("state")}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={TimeZones}
          value={formData.time_zone || "Europe/Dublin"}
          onChange={(event, newValue) => {
            handleChange("time_zone")({
              target: { value: newValue },
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Time Zone" fullWidth required />
          )}
          // ListboxProps={{
          //   style: { maxHeight: 200 },
          // }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Main Heading"
          value={formData.main_heading || ""}
          onChange={handleChange("main_heading")}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Biography"
          multiline
          rows={4}
          value={formData.biography || ""}
          onChange={handleChange("biography")}
        />
      </Grid>
    </Grid>
  );
}
